import { sortAsc } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { NotificationSettingsSchema } from '@strata/react-dialog';
import { Spinner } from '@strata/react-loading-animation';
import { SuggestSingleSelect } from '@strata/react-selects';
import { Tag } from '@strata/react-tags';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import { PrescriberList, useGetPrescriberListsQuery } from 'store/api/auctionApi';
import { NewLiveReferralPreferences } from 'store/api/notificationApi';

export type NewLiveReferralNotificationSchema = {
    newReferralPrescriberListIds?: string[];
} & NotificationSettingsSchema;

export const NewLiveReferralSettings = () => {
    const formik = useFormikContext<NewLiveReferralNotificationSchema>();

    const { data: prescriberListData, isLoading } = useGetPrescriberListsQuery();
    const [prescriberListToAdd, setPrescriberListToAdd] = useState<PrescriberList>();

    return (
        <>
            <div className="flex__gap--medium">
                <SuggestSingleSelect
                    id="prescriberListToAdd"
                    name="prescriberListToAdd"
                    label="Prescriber List"
                    placeholderText="Select Prescriber List"
                    values={prescriberListData?.filter(
                        // Filter out already added lists
                        ({ prescriberListId }) =>
                            !formik.values.newReferralPrescriberListIds?.includes(prescriberListId),
                    )}
                    selectedItem={prescriberListToAdd}
                    onChange={setPrescriberListToAdd}
                    isLoading={isLoading}
                    required
                    verticalSpacingSmall
                />
                <div style={{ marginTop: 19.5 }}>
                    <Button
                        intent={Button.Intent.NONE}
                        label="Add Prescriber List"
                        type="button"
                        disabled={!prescriberListToAdd}
                        onClick={() => {
                            (async () => {
                                if (prescriberListToAdd?.prescriberListId) {
                                    await formik.setFieldValue('newReferralPrescriberListIds', [
                                        ...(formik.values.newReferralPrescriberListIds ?? []),
                                        prescriberListToAdd.prescriberListId,
                                    ]);
                                }
                                setPrescriberListToAdd(undefined);
                            })();
                        }}
                    />
                </div>
            </div>
            {isLoading ? (
                <div
                    className="margin__bottom--medium"
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                >
                    <Spinner size={Spinner.Size.SMALL} intent={Spinner.Intent.NONE} />
                </div>
            ) : (
                (formik.values.newReferralPrescriberListIds ?? []).length > 0 && (
                    <CollapsibleSection
                        className="sds_notification-settings__collapsible-section margin__bottom--medium"
                        title="Selected Prescriber Lists"
                        chevronSize={CollapsibleSection.ChevronSize.SMALL}
                        content={formik.values
                            .newReferralPrescriberListIds!.map((prescriberListId) =>
                                prescriberListData?.find(
                                    (list) => list.prescriberListId === prescriberListId,
                                ),
                            )
                            .filter((list) => !!list)
                            .sort((a, b) => sortAsc(a.listName, b.listName))
                            .map((list) => (
                                <Tag
                                    key={list.prescriberListId}
                                    className="margin__bottom--small margin__right--small"
                                    label={list.listName}
                                    onRemove={() => {
                                        (() =>
                                            formik.setFieldValue(
                                                'newReferralPrescriberListIds',
                                                formik.values.newReferralPrescriberListIds!.filter(
                                                    (prescriberListId) =>
                                                        prescriberListId !== list.prescriberListId,
                                                ),
                                            ))();
                                    }}
                                />
                            ))}
                    />
                )
            )}
        </>
    );
};

NewLiveReferralSettings.initialValues = (preferences?: NewLiveReferralPreferences) => ({
    newReferralPrescriberListIds: preferences?.prescriber_lists ?? [],
});

NewLiveReferralSettings.submitValues = (
    values: NewLiveReferralNotificationSchema,
): NewLiveReferralPreferences => ({
    prescriber_lists: values.newReferralPrescriberListIds,
});
